import React, { useCallback, useEffect, useRef, useState } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import {
    differenceInHours,
    differenceInMinutes,
    differenceInSeconds,
    isFuture,
    isPast,
    parseISO,
    secondsToMilliseconds,
} from 'date-fns';

import ticker$ from 'utils/hooks/useInterval';
import { isWeb } from 'utils/responsiveness';

import LocalizedText from 'components/LocalizedText';

import styles from './styles';
import { Props, Ticker } from './types';
import { getMarkerTextKey } from './utils';

const Marker = ({
    updateFrequency = 5000,
    bgColor = 'red',
    style = {},
    textStyle = {},
    startTime,
    endTime,
    testID,
    expiryDate,
    type,
    comingSoonDate = undefined,
}: Props) => {
    const { t } = useTranslation();
    const ticker = useRef<Ticker | null>(null);
    const [markerText, setMarkerText] = useState<string | null>(
        getMarkerTextKey(startTime, endTime),
    );
    const [visible, setVisible] = useState<boolean>(
        isFuture(secondsToMilliseconds(endTime || 0)),
    );

    const unsetTimer = useCallback(() => {
        if (ticker.current) {
            ticker.current.unsubscribe();
            ticker.current = null;
        }
    }, []);

    const isComingSoon =
        comingSoonDate && isFuture(secondsToMilliseconds(comingSoonDate));

    const onTick = useCallback(() => {
        if (endTime && isPast(secondsToMilliseconds(endTime)) && !expiryDate) {
            setVisible(false);
            unsetTimer();
        } else if (expiryDate && !isComingSoon) {
            const differenceInH = differenceInHours(
                parseISO(expiryDate),
                Date.now(),
            );

            if (differenceInH <= 48 && differenceInH >= 1) {
                setVisible(true);
                setMarkerText(
                    `${t('CONTENT_MARKER_EXPIRES_IN')} ${
                        Math.ceil(differenceInH) + 1
                    }${t('CONTENT_MARKER_EXPIRES_TIME_HOURS')}`,
                );
            } else if (differenceInH < 1) {
                const differenceInM = differenceInMinutes(
                    parseISO(expiryDate),
                    Date.now(),
                );
                const differenceInSec = differenceInSeconds(
                    parseISO(expiryDate),
                    Date.now(),
                );

                if (differenceInSec < 60 && differenceInSec > 0) {
                    setVisible(true);
                    setMarkerText(
                        `${t('CONTENT_MARKER_EXPIRES_IN')} 1${t(
                            'CONTENT_MARKER_EXPIRES_TIME_MINUTES',
                        )}`,
                    );
                } else if (differenceInM > 1) {
                    setVisible(true);
                    setMarkerText(
                        `${t('CONTENT_MARKER_EXPIRES_IN')} ${differenceInM}${t(
                            'CONTENT_MARKER_EXPIRES_TIME_MINUTES',
                        )}`,
                    );
                } else {
                    setVisible(false);
                }
            }
        } else if (isComingSoon) {
            setVisible(true);
            setMarkerText(t('CONTENT_MARKER_COMING_SOON'));
        } else {
            setVisible(true);
            setMarkerText(getMarkerTextKey(startTime, endTime));
        }
    }, [startTime, endTime, expiryDate, unsetTimer, isComingSoon]);

    useEffect(() => {
        if (
            ticker.current === null &&
            ((endTime &&
                isFuture(secondsToMilliseconds(endTime)) &&
                type !== 'vod') ||
                (expiryDate && isFuture(parseISO(expiryDate))) ||
                isComingSoon)
        ) {
            ticker.current = ticker$(updateFrequency).subscribe(() => onTick());
        } else {
            setVisible(false);
        }

        return () => {
            unsetTimer();
        };
    }, [endTime, unsetTimer, onTick, expiryDate, isComingSoon, type]);

    if (!visible) {
        return null;
    }

    return (
        <View style={[{ backgroundColor: bgColor }, style, styles.marker]}>
            <LocalizedText
                testID={testID}
                textKey={markerText}
                style={[styles.markerText, textStyle]}
                {...(isWeb && { tabIndex: 0 })}
                adjustsFontSizeToFit
            />
        </View>
    );
};

export default Marker;
